import { saveAs } from 'file-saver'
import { useState } from 'react'
import { createDocX, getHeader } from '@chegg-wt/core/utils/formattingExport'
import UAParser from 'ua-parser-js'
import { supportPageLinks } from '@chegg-wt/core/constants/supportPageLinks'
import { GoogleDocResponse, googleApi } from '../api/googleDocs'
import { refreshAds } from '../utils/ads'
import { Citation } from '../types/redux-citation-api'

interface GoogleExport {
  shouldShowGoogleModal: boolean
  isSignedIntoGoogle: boolean
  close: () => void
  googleResponse?: GoogleDocResponse
}

interface UseExportCitationsProps {
  exportCitations: (
    style: string,
    exportType: string,
    citations: Citation[],
    onCompleted?: () => void
  ) => Promise<void>
  error: string
  errorCopy: {
    header: string
    description: string
    link: string
    href: string
  }
  googleExport: GoogleExport
}

const cookieErrorHrefByBrowser = {
  Chrome:
    'https://support.google.com/chrome/answer/95647?hl=en#zippy=%2Callow-or-block-cookies',
  Safari:
    'https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac',
  Firefox:
    'https://support.mozilla.org/en-US/kb/websites-say-cookies-are-blocked-unblock-them',
  Edge:
    ' https://support.microsoft.com/search/results?query=Allow+or+Block+Cookies+in+the+new+Microsoft+Edge&isEnrichedQuery=false',
}

export const useExportCitations = (): UseExportCitationsProps => {
  const [error, setError] = useState('')
  const [errorCopy, setErrorCopy] = useState({
    header: 'Issue connecting to Google Drive',
    description: `We weren't able to connect to your account, so we aren't able to export your bibliography.  For more help, please`,
    link: 'visit our support page',
    href: supportPageLinks.easybib,
  })
  const [shouldShowGoogleModal, setShouldShowGoogleModal] = useState(false)
  const [isSignedIntoGoogle, setIsSignedIntoGoogle] = useState(false)
  const [googleResponse, setGoogleResponse] = useState<GoogleDocResponse>()

  const checkShouldPerformExport = async (exportType: string) => {
    switch (exportType) {
      case 'gdocs': {
        await googleApi
          .initGoogleAuth() // this will resolve if already initialized
          .catch((err) => {
            setGoogleResponse({
              error: err.details,
            })

            const parser = new UAParser()
            const browser = parser.getBrowser().name
            const href = cookieErrorHrefByBrowser[browser]

            setErrorCopy({
              header: 'Issues connecting to Google Drive.',
              description: `We weren’t able to connect your account because third-party cookies are not enabled. ${
                href
                  ? 'For help enabling third-party cookies in your browser,'
                  : 'Please enable third-party cookies in your browser and try again.'
              }`,
              link: 'please visit our page.',
              href,
            })
          })
        const isSignedIn = googleApi.isSignedIntoGoogle()

        if (!shouldShowGoogleModal) {
          setShouldShowGoogleModal(true)
          setIsSignedIntoGoogle(isSignedIn)
          return false
        }
        if (!isSignedIn) {
          await googleApi
            .signInToGoogle()
            .catch(() => {
              setGoogleResponse({
                error: 'Unable to signin to google',
              })
            })
            .finally(() => {
              setIsSignedIntoGoogle(googleApi.isSignedIntoGoogle())
            })
          return false // want to return false here still so we separate api calls
        }
        setIsSignedIntoGoogle(true)
        return true
      }
      default:
        return true
    }
  }

  const handleGoogleExportClose = () => {
    setShouldShowGoogleModal(false)
    setGoogleResponse(null)
  }

  const handleExport = async (
    style: string,
    exportType: string,
    citations: Citation[],
    onCompleted?: (data?: any) => void
  ) => {
    if (!style || !exportType || !citations || !citations.length) {
      return
    }

    const shouldPerformExport = await checkShouldPerformExport(exportType)
    if (!shouldPerformExport) {
      return
    }

    refreshAds(exportType === 'gdocs' ? 'exportGoogle' : 'exportWord')

    // get projectId for filename
    const { projectId } = citations[0]

    const docX = await createDocX(style, citations)
    const filename = `${getHeader(style)}_${projectId}.docx`

    try {
      switch (exportType) {
        case 'docx': {
          saveAs(docX, filename)
          break
        }
        case 'gdocs': {
          await googleApi
            .saveToDrive(docX, filename)
            .catch(() => {
              setGoogleResponse({
                error: 'An error occurred exporting citations',
              })
            })
            .then((response) => {
              if (response) {
                setGoogleResponse(response)
                if (response.url) {
                  if (window.open) {
                    const winOpen = window.open()
                    winOpen.opener = null
                    winOpen.location.href = response.url
                  }
                }
              }
            })

          break
        }
        default: {
          return
        }
      }
      setError('')

      if (onCompleted) {
        onCompleted()
      }
    } catch {
      setError('An error occurred exporting citations.')
    }
  }

  return {
    exportCitations: handleExport,
    error,
    errorCopy,
    googleExport: {
      shouldShowGoogleModal,
      isSignedIntoGoogle,
      googleResponse,
      close: handleGoogleExportClose,
    },
  }
}
